import { useState } from "react";
import type { InvitableRole } from "../../types/types";

// Email validation function
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const useInviteUserDialogHook = (
  onSubmit: (userData: {
    email: string;
    name: string;
    role: InvitableRole;
    jobTitle?: string;
    buyer_type?: string;
  }) => Promise<void>,
  onClose: () => void,
  loading: boolean
) => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "learner" as InvitableRole,
    jobTitle: "",
    isDecisionMaker: false,
  });
  const [emailError, setEmailError] = useState("");

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData({ ...formData, email });

    // Real-time email validation
    if (email && !isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, name: e.target.value });
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({
      ...formData,
      role: e.target.value as InvitableRole,
    });
  };

  const handleJobTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, jobTitle: e.target.value });
  };

  // WS-O P1.1: "invite as our purchasing decision-maker" — sends the invitee as an
  // org admin WITH the purchase-capable buyer_type (the backend scoped bypass).
  const handleDecisionMakerChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, isDecisionMaker: e.target.checked });
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.name || !isValidEmail(formData.email)) {
      return;
    }

    // A decision-maker invite is sent as organisation_admin WITH the
    // purchase-capable buyer_type so the invitee can convert the trial; the
    // backend scoped bypass permits this org_admin->org_admin invite for trials.
    await onSubmit({
      email: formData.email,
      name: formData.name,
      role: formData.isDecisionMaker
        ? ("organisation_admin" as InvitableRole)
        : formData.role,
      jobTitle: formData.jobTitle || undefined,
      buyer_type: formData.isDecisionMaker
        ? "makes-purchasing-decisions"
        : undefined,
    });
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setFormData({
      email: "",
      name: "",
      role: "learner",
      jobTitle: "",
      isDecisionMaker: false,
    });
    setEmailError("");
  };

  const isFormValid =
    formData.email && formData.name && isValidEmail(formData.email) && !loading;

  return {
    // State
    formData,
    emailError,

    // Actions
    handleEmailChange,
    handleNameChange,
    handleRoleChange,
    handleJobTitleChange,
    handleDecisionMakerChange,
    handleSubmit,
    handleClose,

    // Computed values
    isFormValid,
  };
};
